/**
 * Session identity — the ONE module allowed to touch the identity
 * localStorage keys (overhaul Wave 2).
 *
 * The PIN/magic-link login flows persist who's signed in via localStorage
 * (Firebase Auth custom tokens carry the claims, but the app's synchronous
 * paths read these keys). Historically 200+ files read the raw keys
 * directly; the contract from Wave 2 onward:
 *
 *   - WRITES go through setSessionIdentity / clearSessionIdentity
 *     (LandingPage.finalizeSignIn, AuthVerifyPage, logout).
 *   - NEW reads go through getSession()/the accessors here, or the
 *     AuthSessionContext's useSession() in React. Existing raw
 *     localStorage reads migrate opportunistically — the count may only
 *     fall (see the overhaul plan's ratchet).
 *
 * Key inventory (unchanged names — external automations and existing
 * reads depend on them):
 *   user_id, user_name, logged_in_user (display name), user_role
 *   ('office' | 'tech' surface), tech_id, tech_name, tenant_id.
 */

export interface SessionIdentity {
  userId: string | null;
  /** Canonical user name (user_name). */
  userName: string | null;
  /** Display name shown in the UI (logged_in_user — historically the same
   *  value as userName; both kept because different readers grew around
   *  each key). */
  displayName: string | null;
  /** Which app surface the user entered ('office' | 'tech'). */
  role: string | null;
  techId: string | null;
  techName: string | null;
  tenantId: string | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Cached snapshot so useSyncExternalStore gets a stable reference. */
let snapshot: SessionIdentity | null = null;

function read(key: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(key);
}

function buildSnapshot(): SessionIdentity {
  return {
    userId: read('user_id'),
    userName: read('user_name'),
    displayName: read('logged_in_user'),
    role: read('user_role'),
    techId: read('tech_id'),
    techName: read('tech_name'),
    tenantId: read('tenant_id'),
  };
}

function notify(): void {
  snapshot = null;
  for (const l of listeners) l();
}

/** Current identity (stable reference until the next change). */
export function getSession(): SessionIdentity {
  if (snapshot === null) snapshot = buildSnapshot();
  return snapshot;
}

/** Subscribe to identity changes (same-tab writes + cross-tab storage
 *  events). Returns an unsubscribe function. */
export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Cross-tab: another tab's login/logout updates this tab's snapshot.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (
      e.key === null ||
      ['user_id', 'user_name', 'logged_in_user', 'user_role', 'tech_id', 'tech_name', 'tenant_id'].includes(e.key)
    ) {
      notify();
    }
  });
}

export interface SignInIdentity {
  userId: string;
  userName: string;
  tenantId: string;
  /** 'office' | 'tech' — which surface this sign-in lands on. */
  role: string;
  /** Set for tech-surface sign-ins. */
  techId?: string;
  techName?: string;
}

/**
 * Persist a sign-in. Writes the exact key set the legacy flows wrote
 * (logged_in_user mirrors user_name, tech_* only when supplied).
 */
export function setSessionIdentity(identity: SignInIdentity): void {
  localStorage.setItem('logged_in_user', identity.userName);
  localStorage.setItem('user_name', identity.userName);
  localStorage.setItem('user_id', identity.userId);
  localStorage.setItem('tenant_id', identity.tenantId);
  localStorage.setItem('user_role', identity.role);
  if (identity.techId) localStorage.setItem('tech_id', identity.techId);
  if (identity.techName) localStorage.setItem('tech_name', identity.techName);
  notify();
}

/** Update just the surface role (dual-access users switching shells). */
export function setSessionRole(role: string): void {
  localStorage.setItem('user_role', role);
  notify();
}

/**
 * Move an already-signed-in user into the FIELD shell.
 *
 * `user_role` is the SURFACE the user is on, not what they are — see the
 * SessionIdentity doc above. `TechAuthGuard` accepts a session whose role is
 * 'tech' or 'guest_tech', so flipping the surface is what lets someone cross
 * without re-entering their PIN. That is the point on a Lite tenant: the
 * person who wrote the quote is the person driving to the job, and asking
 * them to re-authenticate to their own phone is friction, not security.
 *
 * `tech_id` / `tech_name` are stamped from the signed-in identity because a
 * lot of the field UI reads those keys directly rather than falling back to
 * `user_id` / `user_name` (`useTechAuth` does fall back; NotesModule,
 * FlagButton, JobCompletionFormModal and friends do not).
 *
 * NOT a privilege grant. The caller must have checked `useFieldAccess()`
 * first, and Firestore rules are unchanged either way.
 */
export function enterFieldSurface(): void {
  const { userId, userName } = getSession();
  if (userId) localStorage.setItem('tech_id', userId);
  if (userName) localStorage.setItem('tech_name', userName);
  localStorage.setItem('user_role', 'tech');
  notify();
}

/**
 * Move back to the OFFICE shell.
 *
 * Leaves `tech_id` / `tech_name` in place: they cost nothing, and clearing
 * them would strand any field data still being written by an in-flight
 * request from the shell we just left.
 */
export function enterOfficeSurface(): void {
  localStorage.setItem('user_role', 'office');
  notify();
}

/** Clear the identity keys on logout. Leaves non-identity keys (theme,
 *  UI prefs, caches) alone. */
export function clearSessionIdentity(): void {
  for (const key of ['logged_in_user', 'user_name', 'user_id', 'user_role', 'tech_id', 'tech_name']) {
    localStorage.removeItem(key);
  }
  // tenant_id survives logout deliberately — the pre-auth landing page
  // uses it for branding, matching the legacy logout behaviour.
  notify();
}

// The surface-role vocabulary and the users.role → session role mapper moved
// to shared/ so the Claude connector's raise_bug_report writes the same
// `reportedByRole` the form would. Re-exported here so callers don't move.
export { sessionRoleForUserRole, type SessionRole } from '@shared/domain/session-role';
